import { useIntl } from 'react-intl';

import { useAppDispatch } from '@/shared/hooks';
import { debug, getCardRemovalAnimationDuration } from '@/shared/lib';

import { ModalID, modalModel } from '@/entities/modal';
import { notificationModel } from '@/entities/notification';
import { reportsModel } from '@/entities/reports';

type RemoveReportOptions = {
  /** Called when the remove request fails so the caller can roll back optimistic UI. */
  onError?: () => void;
};

/**
 * @deprecated
 */
export const useRemoveReport_DEPRECATED = () => {
  const dispatch = useAppDispatch();

  return async (reportID: string) => {
    await dispatch(
      reportsModel.thunks.deleteReport_DEPRECATED({ ReportID: reportID }),
    ).unwrap();
  };
};

/**
 * Resolves `true` as soon as the user confirms so the card can start its
 * removal animation; the request itself is sent after the animation. A failed
 * request shows an error notification and reports back through `onError`.
 */
export const useRemoveReport = () => {
  const { formatMessage } = useIntl();
  const dispatch = useAppDispatch();

  return async (
    reportIDs: string[],
    options?: RemoveReportOptions,
  ): Promise<boolean> =>
    new Promise((resolve) => {
      dispatch(
        modalModel.actions.openModal({
          modalID: ModalID.Confirm,
          data: {
            title: formatMessage({
              id: 'removeReportModal.title',
              defaultMessage: 'Remove report',
            }),
            okText: formatMessage({
              id: 'global.applyRemove',
              defaultMessage: 'Yes, remove',
            }),
            description: formatMessage({
              id: 'removeReportModal.message',
              defaultMessage:
                'After removing, all report data will be lost. Are you sure?',
            }),
            danger: true,
            onConfirm: async () => {
              dispatch(modalModel.actions.closeModal(ModalID.Confirm));
              resolve(true);

              setTimeout(async () => {
                try {
                  await Promise.all(
                    reportIDs.map((reportID) =>
                      dispatch(
                        reportsModel.thunks.deleteReport(reportID),
                      ).unwrap(),
                    ),
                  );
                } catch (error) {
                  debug('Failed to delete report:', error);

                  dispatch(
                    notificationModel.actions.addNotification({
                      id: Date.now().toString(),
                      type: 'error',
                      message: formatMessage({
                        id: 'removeReport.error',
                        defaultMessage:
                          'Failed to remove the report. Please try again.',
                      }),
                    }),
                  );
                  options?.onError?.();
                }
              }, getCardRemovalAnimationDuration());
            },
            onCancel: () => {
              resolve(false);
            },
          },
        }),
      );
    });
};
